import { useCallback, useEffect, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/local.js';
import { failedEntries, pendingCount, retryFailed, syncOnce } from './engine.js';
import { resealServerPhotos, uploadPendingPhotos } from '../db/photos.js';
import { resealRecipeImages } from '../db/recipeImages.js';
import { refreshEstimateJobs } from '../db/estimates.js';
import { maybeEnsureHouseholdKeys } from '../crypto/householdKeys.js';
import { markUpdateRequired } from '../pwa/updateState.js';
import { heldEmptyCount, repairEnvelopesOnce, resealNewTables } from './e2e.js';
import { currentRole } from '../auth.js';
import type { SyncResult } from './engine.js';

/** Abstand zwischen zwei Runden, solange die App offen ist. */
const INTERVAL_MS = 30_000;

/**
 * Kurz warten nach einer Änderung, damit ein paar Tipps hintereinander in einer
 * Runde gehen. Länger nicht: Bis zum 10.10.2026 wartete eine Änderung auf den
 * nächsten 30-Sekunden-Takt — und wer die App vorher schloss, bis zum nächsten
 * Öffnen. Ein „Genommen" kam so neun Minuten zu spät, und der Server fasste nach.
 */
export const SEND_DELAY_MS = 400;

export interface RefreshOutcome {
  result: SyncResult | null;
  pending: number;
  failed: number;
  held: number;
  online: boolean;
}

export interface SyncState {
  /** Änderungen, die noch nicht beim Server sind. */
  pending: number;
  /** Änderungen, die der Server begründet abgelehnt hat. */
  failed: number;
  /** Leer zurückgehalten — hätten beim Server einen Eintrag geleert (27.10.2026). */
  held: number;
  isSyncing: boolean;
  isOnline: boolean;
  lastResult: SyncResult | null;
  lastSyncedAt: Date | null;
  /** Grund der letzten Ablehnung — gehört in die Oberfläche, nicht ins Nichts. */
  failedReason: string | null;
  syncNow: () => void;
  /** Ziehen zum Aktualisieren: abgleichen und sagen, wie es ausging. */
  refresh: () => Promise<RefreshOutcome>;
  /** Abgelehnte Änderungen erneut freigeben und sofort senden. */
  retryNow: () => void;
}

export function useSync(enabled: boolean): SyncState {
  const [isSyncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState<SyncResult | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [isOnline, setOnline] = useState(() => navigator.onLine);
  const running = useRef(false);
  // Während einer laufenden Runde angefordert: danach gleich noch eine, sonst
  // bliebe die neue Änderung bis zum nächsten Takt liegen.
  const again = useRef(false);

  const pending = useLiveQuery(() => pendingCount(db), [], 0);
  const failed = useLiveQuery(async () => (await failedEntries(db)).length, [], 0);
  const held = useLiveQuery(() => heldEmptyCount(db), [], 0);
  const failedReason = useLiveQuery(
    async () => (await failedEntries(db))[0]?.lastError ?? null,
    [],
    null,
  );

  const run = useCallback(async (): Promise<SyncResult | null> => {
    // Überlappende Runden würden dieselbe Änderung zweimal senden.
    if (!enabled) return null;
    if (running.current) {
      again.current = true;
      return null;
    }
    running.current = true;
    setSyncing(true);

    let result: SyncResult;
    try {
      result = await syncOnce();
      // Bilder nach der Runde: Erst muss ihre Beschreibung beim Server sein.
      if (!result.error) {
        const photos = await uploadPendingPhotos(db).catch(() => null);
        // Hochgeladen: gleich noch eine Runde, die den Vermerk vom Server holt.
        if (photos && photos.uploaded > 0) again.current = true;
      }
      // Nach dem Einrichten (E21): schon hochgeladene Bilder verschlüsseln.
      if (!result.error) await resealServerPhotos(db).catch(() => undefined);
      // Rezeptbilder ebenso — auch geteilte, die mit dem Haushaltsschlüssel kamen.
      if (!result.error) await resealRecipeImages(db).catch(() => undefined);
      // Haushaltsschlüssel holen, anlegen, nachreichen (E21).
      if (!result.error) await maybeEnsureHouseholdKeys(db).catch(() => undefined);
      // Hüllen einer älteren Fassung zurücklegen — vor jedem Neusenden (E21).
      if (!result.error) await repairEnvelopesOnce(db).catch(() => 0);
      // Später verschlüsselte Tabellen: Bestand einmal neu senden (E21).
      if (!result.error) await resealNewTables(db, currentRole() === 'child').catch(() => 0);
      // Foto-Schätzung im Hintergrund (28.10.2026): Ergebnisse holen, auch
      // ohne Mitteilung — und Ungesendetes erneut senden. Nach den Schlüsseln:
      // geöffnet wird mit dem eigenen Schlüsselpaar.
      if (!result.error) await refreshEstimateJobs(db).catch(() => undefined);
      // Zu alte Fassung (426): nach der neuen sehen und laden, sobald es ruhig ist.
      if (result.errorKind === 'update') markUpdateRequired();
      setLastResult(result);
      if (!result.error) setLastSyncedAt(new Date());
    } finally {
      running.current = false;
      setSyncing(false);
    }
    if (again.current) {
      again.current = false;
      void run();
    }
    return result;
  }, [enabled]);

  const syncNow = useCallback(() => void run(), [run]);

  /**
   * Ziehen zum Aktualisieren (24.10.2026): eine eigene Runde — läuft gerade
   * eine, wird auf sie gewartet und danach noch einmal abgeglichen. Gibt
   * zurück, was die Oberfläche danach sagen soll.
   */
  const refresh = useCallback(async (): Promise<RefreshOutcome> => {
    while (running.current) await new Promise((resolve) => setTimeout(resolve, 100));
    const result = await run();
    const [pendingNow, failedNow, heldNow] = await Promise.all([
      pendingCount(db),
      failedEntries(db),
      heldEmptyCount(db),
    ]);
    return {
      result,
      pending: pendingNow,
      failed: failedNow.length,
      held: heldNow,
      online: navigator.onLine,
    };
  }, [run]);

  const retryNow = useCallback(() => {
    void (async () => {
      await retryFailed();
      await run();
    })();
  }, [run]);

  useEffect(() => {
    if (!enabled) return;

    void run();
    const timer = setInterval(() => void run(), INTERVAL_MS);

    // Sobald das Netz zurück ist, sofort — nicht erst beim nächsten Takt.
    const goOnline = () => {
      setOnline(true);
      void run();
    };
    const goOffline = () => setOnline(false);
    // Zurück im Vordergrund heißt meist: es ist Zeit vergangen. Und auf dem
    // Weg in den Hintergrund ist es die letzte Gelegenheit, bevor iOS die App
    // einfriert — was dann noch in der Outbox liegt, wartet bis zum nächsten Öffnen.
    const onVisible = () => void run();

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, run]);

  // Jede neue Änderung in der Outbox löst eine Runde aus. Ohne Netz scheitert
  // sie, die Zahl bleibt gleich — dann wird nicht erneut ausgelöst, sondern auf
  // „online" oder den Takt gewartet.
  useEffect(() => {
    if (!enabled || !pending) return;
    const timer = setTimeout(() => void run(), SEND_DELAY_MS);
    return () => clearTimeout(timer);
  }, [enabled, pending, run]);

  return {
    pending: pending ?? 0,
    failed: failed ?? 0,
    held: held ?? 0,
    isSyncing,
    isOnline,
    lastResult,
    lastSyncedAt,
    failedReason: failedReason ?? null,
    syncNow,
    retryNow,
    refresh,
  };
}
